import { useState, useRef, useEffect } from "react";
import { connect, ConnectedProps} from "react-redux";
import { logout } from "../Auth/authThunks";
import { PATH } from "../../Constants/paths";
import { useNavigate, useLocation } from "react-router";
import { getUserFromLS } from "../../utils/getUserFromLS";
import { formatEnumValue } from "../../utils/formatters";
import NotificationDrawer from "../Common/Firestore/Alerts/NotificationDrawer";
import { useAlerts } from "../Common/Firestore/Alerts/AlertContext";
import { TrialBannerHeaderChip } from "../Billing/TrialBannerHeaderChip";

const mapStateToProps = (state: AppState) => ({
  isAuthenticated: state.auth.isAuthenticated,
});
const mapDispatchToProps = { logout };
const connector = connect(mapStateToProps, mapDispatchToProps);
type Props = ConnectedProps<typeof connector>;

const AltHeaderContent = (props: Props) => {
  const { logout, isAuthenticated } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUserFromLS();
  const isOnLanding = location.pathname === PATH.LANDING;
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { unreadAlerts } = useAlerts();
  const alertCount = unreadAlerts.length;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const handleLogin = async () => {
    await logout();
    navigate(PATH.LOGIN);
  };

  const handleHomeClick = () => {
    navigate(PATH.Sales_Home);
  };

  const handleNotificationsClick = () => {
    setMenuOpen(false);
    setDrawerOpen(true);
  };

  return (
    <>
      <div className="header_container">
        <a href="/">
          <img
            className="header_logo"
            alt="Intentico"
            loading="eager"
            src="/intenticoImages/logo_intentico_light.svg"
          />
        </a>
        <div className="header_rightAlignedInfoContainer">
          {isAuthenticated && !isOnLanding ? (
            <div className="header_accountCluster">
              <TrialBannerHeaderChip />
              <div className="header_userMenuWrapper" ref={menuRef}>
              <button
                type="button"
                className="header_userMenuTrigger"
                onClick={() => setMenuOpen((prev) => !prev)}
                aria-expanded={menuOpen}
                aria-haspopup="true"
              >
                <span className="header_userIcon">
                  <i className="fa-solid fa-user" aria-hidden />
                  {alertCount > 0 && (
                    <span className="header_userIconBadge" aria-label={`${alertCount} unread notifications`}>
                      {alertCount > 99 ? "99+" : alertCount}
                    </span>
                  )}
                </span>
                <div className="header_userInfoColumn">
                  <p className="header_userNameText">{user.full_name}</p>
                  <p className="header_userRoleText">{user.role ? formatEnumValue(user.role) : ''}</p>
                </div>
              </button>

              {menuOpen && (
                <div className="header_dropdownMenu" role="menu">
                  <div className="header_dropdownPanelHeader">
                    <p className="header_dropdownKicker">Account</p>
                    <p className="header_dropdownUserName">{user.full_name}</p>
                    {user.role ? (
                      <p className="header_dropdownUserRole">
                        {formatEnumValue(user.role)}
                      </p>
                    ) : null}
                  </div>
                  <div className="header_dropdownMenuBody">
                  <button
                    type="button"
                    className="header_dropdownItem"
                    role="menuitem"
                    onClick={handleNotificationsClick}
                  >
                    <i className="fa-solid fa-bell" aria-hidden />
                    <span>Notifications</span>
                    {alertCount > 0 && (
                      <span className="header_dropdownBadge">
                        {alertCount > 99 ? "99+" : alertCount}
                      </span>
                    )}
                  </button>
                  </div>
                </div>
              )}
              </div>
            </div>
          ) : isAuthenticated && isOnLanding ? (
            <button
              type="button"
              className="header_iconBtn"
              onClick={handleHomeClick}
              aria-label="Go to sales home"
            >
              <i className="fa-duotone fa-solid fa-home" aria-hidden />
            </button>
          ) : (
            <button
              type="button"
              className="header_iconBtn"
              onClick={handleLogin}
              aria-label="Log in"
            >
              <i className="fa-duotone fa-solid fa-arrow-right-to-bracket" aria-hidden />
            </button>
          )}
        </div>
      </div>
      <NotificationDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
};

export const AltHeader = connector(AltHeaderContent);
